/**
 * useRecentPages — tracks the user's page navigation across the app.
 *
 * • Fires on every route change to `/app/*` (skipping noisy routes)
 * • Persists locally (LocalStorage per workspace) for instant reads
 * • Best-effort backend sync via POST /workspaces/{ws}/recent-pages
 *   so a user's trail follows them across devices.
 *
 * The current page title is inferred from the `<PageHeader>` `<h1>` or the
 * document `<title>` if available; falls back to a route-based label.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

// Routes we DO NOT want to appear in "Recently viewed" (noisy or trivial).
const SKIP_PREFIXES = [
  "/app$",             // exact bulletin board (home)
  "/app/notifications",
  "/app/inbox",
  "/app/billing",
  "/login",
  "/signup",
];

// Human-friendly labels when we can't infer from headings.
const ROUTE_LABELS = {
  "/app/projects": ["Projects", "📁"],
  "/app/tasks": ["Tasks", "✅"],
  "/app/action-items": ["Action items", "🎯"],
  "/app/meetings": ["Meetings", "📅"],
  "/app/calendar": ["Calendar", "🗓️"],
  "/app/clients": ["Clients", "🏢"],
  "/app/leads": ["Leads", "🎯"],
  "/app/prospecting": ["Prospecting", "🔍"],
  "/app/marketing": ["Marketing", "📢"],
  "/app/brands": ["Brands", "💎"],
  "/app/packages": ["Packages", "📦"],
  "/app/content-calendar": ["Content Calendar", "🗓️"],
  "/app/delivery-intelligence": ["Delivery Intel", "📊"],
  "/app/sales": ["Sales & invoices", "💰"],
  "/app/finance": ["Finance", "💼"],
  "/app/finance-copilot": ["Finance Copilot", "🤖"],
  "/app/finance-reports": ["Finance Reports", "📈"],
  "/app/finance-close": ["Finance Close", "🔒"],
  "/app/finance-vendors": ["Vendors & Bills", "🧾"],
  "/app/finance-collections": ["AR Collections", "💸"],
  "/app/finance-assets": ["Fixed Assets", "🏗️"],
  "/app/finance-leadership": ["CFO Dashboard", "🧭"],
  "/app/goals": ["Goals & OKRs", "🎯"],
  "/app/timesheet": ["Timesheet", "⏱️"],
  "/app/attendance": ["Attendance & Leave", "📆"],
  "/app/employees": ["Employees", "👥"],
  "/app/people-analytics": ["People Analytics", "📊"],
  "/app/hr-dashboard": ["HR Dashboard", "👔"],
  "/app/departments": ["Departments", "🏛️"],
  "/app/users": ["User management", "👥"],
  "/app/roles": ["Roles & permissions", "🔐"],
  "/app/quality-review": ["Quality", "⚖️"],
  "/app/leaderboard": ["Leaderboard", "🏆"],
  "/app/storage": ["Storage", "💾"],
  "/app/skill-center": ["Skill Center", "🎓"],
  "/app/compliance": ["Compliance", "📋"],
  "/app/vault": ["Vault", "🔒"],
  "/app/sops": ["SOPs", "📖"],
  "/app/log": ["Log", "⚠️"],
  "/app/log/dashboard": ["Log dashboard", "📊"],
  "/app/settings": ["Settings", "⚙️"],
  "/app/integrations": ["Integrations", "🔌"],
  "/app/team": ["Team", "👥"],
  "/app/map": ["Workspace map", "🗺️"],
  "/app/activity": ["Activity", "📡"],
  "/app/escalations": ["Escalations", "🚨"],
  "/app/auto-response": ["Auto-response", "🤖"],
  "/app/referrals": ["Referrals", "🎁"],
  "/app/profile": ["Profile", "👤"],
  // ────── Iter 106: missing labels ──────
  "/app/helpdesk": ["Helpdesk", "🎫"],
  "/app/company-qa": ["Company Q&A", "❓"],
  "/app/policies": ["Company Policies", "📜"],
  "/app/engagement": ["Engagement", "🎉"],
  "/app/buddy": ["Buddy Program", "🤝"],
  "/app/onboarding": ["Onboarding", "🚀"],
  "/app/offboarding": ["Offboarding", "👋"],
  "/app/announcements": ["Announcements", "📣"],
  "/app/bulletin": ["Bulletin Board", "📌"],
  "/app/directory": ["Directory", "🗂️"],
  "/app/celebrations": ["Celebrations", "🎂"],
  "/app/dashboard": ["Dashboard", "📊"],
  "/app/reports": ["Reports", "📈"],
  "/app/analytics": ["Analytics", "📊"],
  "/app/expenses": ["Expenses", "💳"],
  "/app/purchase-orders": ["Purchase Orders", "🧾"],
  "/app/reviews": ["Reviews & approvals", "✅"],
  "/app/annotations": ["Annotations", "✏️"],
  "/app/campaigns": ["Campaigns", "📣"],
  "/app/sequences": ["Sequences", "📧"],
  "/app/pipeline": ["Sales pipeline", "🎯"],
  "/app/inbox-shared": ["Shared inbox", "📥"],
  "/app/wiki": ["Company Wiki", "📚"],
  "/app/training": ["Training", "🎓"],
  "/app/feedback": ["Feedback", "💬"],
};

// Turn "/app/helpdesk/settings/foo" → "Helpdesk · Settings · Foo".
function prettifySegment(seg) {
  if (!seg) return "";
  // UUID/id-like segments → skip
  if (/^[0-9a-f]{8}-/.test(seg) || /^[0-9]{6,}$/.test(seg)) return "";
  return seg.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettifyPath(pathname) {
  const clean = pathname.replace(/^\/app\/?/, "");
  if (!clean) return "Home";
  return clean.split("/").map(prettifySegment).filter(Boolean).join(" · ") || "Home";
}

function shouldSkip(pathname) {
  for (const p of SKIP_PREFIXES) {
    if (p.endsWith("$")) {
      if (pathname === p.slice(0, -1)) return true;
    } else if (pathname.startsWith(p)) return true;
  }
  return false;
}

function inferTitleAndIcon(pathname) {
  // Exact match
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  // Prefer H1 that's visible on the page (works for /app/projects/xyz where
  // the page title is the project name).
  const h1 = typeof document !== "undefined"
    ? document.querySelector("[data-testid$='-header'] h1, main h1")?.textContent?.trim()
    : null;
  // Prefix match (e.g. /app/projects/xyz → "Project · <H1 title>")
  for (const key of Object.keys(ROUTE_LABELS).sort((a, b) => b.length - a.length)) {
    if (pathname.startsWith(key + "/")) {
      const [label, icon] = ROUTE_LABELS[key];
      return [h1 && h1.length < 80 ? `${label} · ${h1}` : label, icon];
    }
  }
  // Fallback (no exact / prefix match): use H1, then document title,
  // then a *pretty* segment-based label — never the raw pathname.
  if (h1 && h1.length < 80) return [h1, "📄"];
  const docTitle = typeof document !== "undefined" ? document.title : null;
  if (docTitle && docTitle !== "Acquilix") {
    return [docTitle.replace(" · Acquilix", ""), "📄"];
  }
  return [prettifyPath(pathname), "📄"];
}

export default function useRecentPages() {
  const { activeWorkspaceId, user } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!activeWorkspaceId || !user?.id) return;
    if (shouldSkip(pathname)) return;

    // Small delay so H1 has rendered before we scrape the title.
    const t = setTimeout(() => {
      const [title, icon] = inferTitleAndIcon(pathname);
      const key = `acquilix.recent.${activeWorkspaceId}.${user.id}`;

      // 1) LocalStorage upsert (instant, per-browser)
      let list = [];
      try {
        list = JSON.parse(localStorage.getItem(key) || "[]");
      } catch { list = []; }
      list = list.filter((r) => r.path !== pathname);
      list.unshift({
        path: pathname, title, icon,
        visited_at: new Date().toISOString(),
      });
      list = list.slice(0, 12);
      try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* quota */ }

      // 2) Best-effort backend sync (won't block UI on failure)
      api.post(`/workspaces/${activeWorkspaceId}/recent-pages`, {
        path: pathname, title, icon,
      }).catch(() => { /* silent */ });

      // 3) Notify widget listeners
      window.dispatchEvent(new CustomEvent("recent-pages:updated"));
    }, 400);
    return () => clearTimeout(t);
  }, [pathname, activeWorkspaceId, user?.id]);
}
